import {assert} from 'Asserts';
import {ProcessFlowchartNodeDetailsType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {PLUGIN_REGISTRY_INSTANCE} from 'processes/graph-builder-processes/platform/PluginRegistry';

import {
  TaskDefinitionVersionSpecWithTaskSchemaFlowchart,
  TaskSchemaNodeSectionType,
} from './processFlowchartConfigEditorConstants';
import {convertStaticFlowchartToSpecFlowchartWithTaskSchema} from './processFlowchartConfigEditorNetworkUtils';
import {
  isStaticTaskSchemaNodeData,
  TaskDefinitionVersionSpecWithTaskSchema,
  TaskSchemaStaticNodeData,
} from './processFlowchartConfigEditorNodeTypes';
import {FlowchartNodeChange, FlowchartNodeChangeType} from './processFlowchartConfigEditorUtils';
import {FlowchartConfigWithMetadata} from './redux/flowchartConfigAutosaveManagerCallbacks';

export default function computeTaskDefinitionVersionUpdateChanges(
  flowchartDraftState: FlowchartConfigWithMetadata,
  selectedNodeId: string,
  updater: (
    currentFlowchart: TaskDefinitionVersionSpecWithTaskSchemaFlowchart
  ) => TaskDefinitionVersionSpecWithTaskSchema<unknown>,
  shouldIncrementNodeNames: boolean
): {nodeChanges: Array<FlowchartNodeChange>} | Record<string, never> {
  const selectedNodeData = flowchartDraftState.staticFlowchart.nodesStaticData.find(
    (node) => node.id === selectedNodeId
  );
  if (selectedNodeData == null) {
    return {};
  }
  assert(isStaticTaskSchemaNodeData(selectedNodeData), 'Expected node to be a static task node');

  const currentFlowchart = convertStaticFlowchartToSpecFlowchartWithTaskSchema(
    flowchartDraftState.staticFlowchart
  );

  let newTaskDefinitionVersionSpec = updater(currentFlowchart);
  const adapter = PLUGIN_REGISTRY_INSTANCE.getTaskSchemaNodeAdapter(
    selectedNodeData.taskDefinitionVersionSpec.newProcessTaskDefinitionVersion.taskSchema.executionType
  );

  // Find all nodes that share the same task definition version and update them all.
  // For singleton schemas (like procedure unit operations), all nodes share the same taskSchemaId
  // but have unique task definition versions, so we must match by the definition version ID.
  const taskDefinitionVersionClientId = selectedNodeData.taskDefinitionVersionSpec.clientId;
  const nodesToUpdate = flowchartDraftState.staticFlowchart.nodesStaticData.filter(
    (node): node is TaskSchemaStaticNodeData =>
      isStaticTaskSchemaNodeData(node) &&
      node.taskDefinitionVersionSpec.clientId === taskDefinitionVersionClientId
  );

  newTaskDefinitionVersionSpec =
    adapter.updateTaskDefinitionVersionWithApplicationData?.(newTaskDefinitionVersionSpec) ??
    newTaskDefinitionVersionSpec;

  const nodeIdsBeingUpdated = new Set(nodesToUpdate.map((node) => node.id));
  const existingNodeNames = shouldIncrementNodeNames
    ? flowchartDraftState.staticFlowchart.nodesStaticData
        .filter((node) => !nodeIdsBeingUpdated.has(node.id))
        .map((node) => node.nodeName)
    : undefined;

  const nodeChanges: Array<FlowchartNodeChange> = nodesToUpdate.map((nodeToUpdate) => {
    const newSections = adapter.createSections(newTaskDefinitionVersionSpec, nodeToUpdate.nodeDetailsType);

    const oldMapFromStart = nodeToUpdate.sections.find(
      (section) => section.sectionType === TaskSchemaNodeSectionType.MAP_FROM_START
    );
    const newMapFromStart = newSections.find(
      (section) => section.sectionType === TaskSchemaNodeSectionType.MAP_FROM_START
    );
    if (oldMapFromStart && newMapFromStart) {
      newMapFromStart.sectionRows = oldMapFromStart.sectionRows;
    }

    const nodeName = adapter.getNodeName(
      newTaskDefinitionVersionSpec,
      nodeToUpdate.nodeDetailsType === ProcessFlowchartNodeDetailsType.OUTPUT_NODE_DETAILS,
      existingNodeNames
    );

    existingNodeNames?.push(nodeName);

    return {
      type: FlowchartNodeChangeType.REPLACE as const,
      id: nodeToUpdate.id,
      data: {
        ...nodeToUpdate,
        taskDefinitionVersionSpec: newTaskDefinitionVersionSpec,
        nodeName,
        sections: newSections,
      },
    };
  });

  return {nodeChanges};
}
